import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { AttributeValueInput } from "../../utils/attributeMapping/types";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  NEUROLOGICAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Variant C of Sparrow Diabetes Screener
 *
 * A/B test variant: Replaces the medication question (Q4) with an
 * AI-powered free-text medication entry. Users type their medications
 * in natural language, and the AI recognizes and counts them.
 * All other questions are identical to the control.
 *
 * Accessible at: /t2d-ec-c
 */
export const sparrowDiabetesScreeningQuestionsC: PreScreeningQuestion[] = [
  // Q1: Diabetes Type
  {
    id: "diabetes_type",
    type: QuestionType.RADIO,
    question: "What type of diabetes do you have?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type2",
        label: "Type 2 diabetes",
      },
      {
        value: "type1",
        label: "Type 1 diabetes",
        disqualify: true,
      },
      {
        value: "no_diabetes",
        label: "I don't have diabetes",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants with type 2 diabetes.",
    attributeMapping: mappers.value({
      type2: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
      ],
      type1: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true },
      ],
      no_diabetes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
      ],
    }),
  },

  // Q2: Diabetes Duration
  {
    id: "diabetes_duration",
    type: QuestionType.RADIO,
    question: "When were you diagnosed with type 2 diabetes?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "less_than_1_year",
        label: "Less than 1 year ago",
        disqualify: true,
      },
      {
        value: "more_than_1_year",
        label: "More than 1 year ago",
      },
    ],
    disqualifyMessage:
      "This study requires participants who have been diagnosed with type 2 diabetes for at least 12 months.",
    attributeMapping: mappers.value({
      more_than_1_year: [
        { attributeDefinitionId: 25713, booleanAttributeValue: true }, // has_type2diabetes_12_months
      ],
      less_than_1_year: [
        { attributeDefinitionId: 25713, booleanAttributeValue: false },
      ],
    }),
  },

  // Q3: A1c Level
  {
    id: "a1c_level",
    type: QuestionType.RADIO,
    question: "What is your most recent A1c (HbA1c) level?",
    helpText:
      "A1c is a blood test that measures your average blood sugar levels over the past 3 months.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "less_than_7",
        label: "Less than 7%",
        disqualify: true,
      },
      {
        value: "7_or_more",
        label: "7% or more",
      },
      {
        value: "not_sure",
        label: "I don't know my A1c level",
      },
    ],
    disqualifyMessage:
      "This study requires participants with an A1c level of 7% or higher.",
    attributeMapping: mappers.value({
      less_than_7: [],
      "7_or_more": [],
      not_sure: [],
    }),
  },

  // Q4: AI Medication Entry (VARIANT C — free-text AI recognition)
  {
    id: "diabetes_medications_count",
    type: QuestionType.AI_MEDICATION_ENTRY,
    question: "What diabetes medications are you currently taking?",
    helpText:
      "Type the names of your diabetes medications. If you take a combination pill (like Janumet or Synjardy), just type its name — we'll recognize the individual medications in it.",
    required: true,
    allowBack: true,
    aiConfig: {
      studyContext: "sparrow-t2d",
      experimentName: "sparrow_t2d_ai_medications",
      minimumCategories: 2,
      minimumCategoriesMessage:
        "This study requires participants who are currently taking at least 2 diabetes medications.",
      medicationCategories: [
        {
          category: "metformin",
          medications: [
            "Metformin", "Glucophage", "Glucophage XR", "Fortamet", "Glumetza", "Riomet",
          ],
        },
        {
          category: "glp1",
          medications: [
            "Ozempic", "Wegovy", "Semaglutide", "Rybelsus",
            "Mounjaro", "Zepbound", "Tirzepatide",
            "Victoza", "Saxenda", "Liraglutide",
            "Trulicity", "Dulaglutide",
            "Byetta", "Bydureon", "Bydureon BCise", "Exenatide",
            "Adlyxin", "Lixisenatide",
            "GLP-1", "GLP1", "GLP-1 agonist",
          ],
        },
        {
          category: "sglt2",
          medications: [
            "Jardiance", "Empagliflozin",
            "Farxiga", "Dapagliflozin",
            "Invokana", "Canagliflozin",
            "Steglatro", "Ertugliflozin",
            "SGLT2", "SGLT-2", "SGLT2 inhibitor",
          ],
        },
        {
          category: "dpp4",
          medications: [
            "Januvia", "Sitagliptin",
            "Tradjenta", "Linagliptin",
            "Onglyza", "Saxagliptin",
            "Nesina", "Alogliptin",
            "DPP-4", "DPP4", "DPP-4 inhibitor",
          ],
        },
        {
          category: "insulin",
          medications: [
            "Insulin",
            "Lantus", "Levemir", "Tresiba", "Toujeo", "Basaglar", "Semglee",
            "Humalog", "Novolog", "Apidra", "Fiasp", "Admelog", "Lyumjev",
            "Humulin", "Novolin",
            "NPH", "Regular insulin",
            "Afrezza",
            "Insulin pen", "Insulin pump", "Insulin injection",
          ],
        },
        {
          category: "sulfonylurea",
          medications: [
            "Glipizide", "Glucotrol",
            "Glyburide", "Glynase", "DiaBeta", "Micronase",
            "Glimepiride", "Amaryl",
            "Sulfonylurea",
          ],
        },
        {
          category: "pioglitazone",
          medications: [
            "Pioglitazone", "Actos",
            "Thiazolidinedione", "TZD",
          ],
        },
        {
          category: "meglitinide",
          medications: [
            "Repaglinide", "Prandin",
            "Nateglinide", "Starlix",
            "Meglitinide",
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study requires participants who are currently taking at least 2 diabetes medications.",
    attributeMapping: (response: unknown): AttributeValueInput[] => {
      if (typeof response !== "string") return [];

      try {
        const parsed = JSON.parse(response);
        const medications = parsed.medications || {};
        const medCount = Object.keys(medications).length;

        return [
          {
            attributeDefinitionId: 26112, // has_diabetes_is_taking_2_diabetic_medications
            booleanAttributeValue: medCount >= 2,
            isSkipped: false,
          },
          {
            attributeDefinitionId: 23960, // has_diabetic_medications_3_current
            booleanAttributeValue: medCount >= 3,
            isSkipped: false,
          },
        ];
      } catch {
        return [];
      }
    },
  },

  // Q5: Diabetes Complications
  {
    id: "diabetes_complications",
    type: QuestionType.CHECKBOX,
    question: "Due to your diabetes, do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "kidney_disease_diabetes",
        label: "I have kidney disease as a result of my diabetes",
      },
      {
        value: "foot_ulcers_diabetes",
        label: "I have foot ulcers as a result of my diabetes",
      },
      {
        value: "nerve_damage_diabetes",
        label: "I have nerve damage as a result of my diabetes",
      },
      {
        value: "eye_disease_diabetes",
        label: "I have eye disease as a result of my diabetes",
      },
      {
        value: "none_diabetes_complications",
        label: "None of these apply to me",
      },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        kidney_disease_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: true },
        ],
        foot_ulcers_diabetes: [
          { attributeDefinitionId: 21301, booleanAttributeValue: true }, // has_diabetic_neuropathy_ulcers
        ],
        nerve_damage_diabetes: [
          { attributeDefinitionId: 20412, booleanAttributeValue: true }, // has_diabeticneuropathy
        ],
        eye_disease_diabetes: [
          { attributeDefinitionId: 20416, booleanAttributeValue: true }, // has_diabeticeyedisease
        ],
      },
      noneValue: "none_diabetes_complications",
      noneAttributeMap: {
        none_diabetes_complications: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETIC_NEPHROPATHY, booleanAttributeValue: false },
          { attributeDefinitionId: 21301, booleanAttributeValue: false }, // has_diabetic_neuropathy_ulcers
          { attributeDefinitionId: 20412, booleanAttributeValue: false }, // has_diabeticneuropathy
          { attributeDefinitionId: 20416, booleanAttributeValue: false }, // has_diabeticeyedisease
        ],
      },
    }),
  },

  // Q6: Health Conditions
  {
    id: "qualifying_conditions_have",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following health conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cardiovascular_history",
        label: "History of heart attack, stroke, angina, or cardiovascular procedures (stents, bypass, angioplasty)",
      },
      {
        value: "osteoporosis",
        label: "Osteoporosis (brittle bones)",
      },
      {
        value: "hypertension_two_meds",
        label: "High blood pressure requiring 2 or more blood pressure medications",
      },
      {
        value: "cushings_with_surgery_status",
        label: "Cushing's syndrome that has not been successfully treated with surgery",
      },
      {
        value: "none_conditions",
        label: "None of these apply to me",
      },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cardiovascular_history: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        osteoporosis: [
          { attributeDefinitionId: 21016, booleanAttributeValue: true }, // has_osteoporosis
        ],
        hypertension_two_meds: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
        ],
        cushings_with_surgery_status: [
          { attributeDefinitionId: 21521, booleanAttributeValue: true }, // has_cushings
        ],
      },
      noneValue: "none_conditions",
      noneAttributeMap: {
        none_conditions: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: 21016, booleanAttributeValue: false }, // has_osteoporosis
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
          { attributeDefinitionId: 21521, booleanAttributeValue: false }, // has_cushings
        ],
      },
    }),
  },

  // Q7: Qualifying Medications (auto-qualification checks AI JSON answer from Q4)
  {
    id: "qualifying_medications_take",
    type: QuestionType.CHECKBOX,
    question: "Are you currently taking any of these medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "insulin_daily",
        label: "Daily insulin injections",
      },
      {
        value: "glp1_medication",
        label: "GLP-1 injections (Ozempic, Mounjaro, Wegovy, Victoza, etc.)",
      },
      {
        value: "none_medications",
        label: "Neither of the above",
        disqualify: (allAnswers: Record<string, string | string[] | number | null | undefined>) => {
          // Parse AI medication entry JSON to get medication count
          const medicationAnswer = allAnswers?.["diabetes_medications_count"];
          let medCount = 0;

          if (typeof medicationAnswer === "string") {
            try {
              const parsed = JSON.parse(medicationAnswer);
              const medications = parsed.medications || {};
              medCount = Object.keys(medications).length;
            } catch {
              medCount = 0;
            }
          } else if (Array.isArray(medicationAnswer)) {
            // Fallback for checkbox format (shouldn't happen in variant C)
            medCount = medicationAnswer.filter((s) => s !== "none_diabetes_meds").length;
          }

          // If they're taking 3+ medications, they auto-qualify - don't disqualify
          if (medCount >= 3) {
            return false;
          }

          // Otherwise, disqualify ONLY if they selected "none" in ALL THREE qualifying questions
          const q5Answers = allAnswers?.["diabetes_complications"] as string[];
          const q6Answers = allAnswers?.["qualifying_conditions_have"] as string[];
          const q7Answers = allAnswers?.["qualifying_medications_take"] as string[];

          // Check if "none" was selected in Q5 (diabetes complications)
          const q5None = Array.isArray(q5Answers) && q5Answers.includes("none_diabetes_complications") && q5Answers.length === 1;

          // Check if "none" was selected in Q6 (health conditions)
          const q6None = Array.isArray(q6Answers) && q6Answers.includes("none_conditions") && q6Answers.length === 1;

          // Check if "none" was selected in Q7 (medications)
          const q7None = Array.isArray(q7Answers) && q7Answers.includes("none_medications") && q7Answers.length === 1;

          // Disqualify only if ALL three are "none"
          return q5None && q6None && q7None;
        },
      },
    ],
    disqualifyMessage:
      "This study requires participants to have at least one of the listed qualifying conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        insulin_daily: [
          { attributeDefinitionId: 23574, booleanAttributeValue: true }, // has_insulin_daily
        ],
      },
      noneValue: "none_medications",
      noneAttributeMap: {
        none_medications: [
          { attributeDefinitionId: 23574, booleanAttributeValue: false }, // has_insulin_daily
        ],
      },
    }),
  },

  // Q8: Blood Disorders (exclusion)
  {
    id: "blood_disorders",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following blood conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "hemolytic_anemia",
        label: "Hemolytic anemia",
        disqualify: true,
      },
      {
        value: "sickle_cell",
        label: "Sickle cell disease",
        disqualify: true,
      },
      {
        value: "itp",
        label: "Immune Thrombocytopenic Purpura (ITP)",
        disqualify: true,
      },
      {
        value: "none_blood_disorders",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants with these blood conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        hemolytic_anemia: [],
        sickle_cell: [],
        itp: [],
      },
      noneValue: "none_blood_disorders",
      noneAttributeMap: {
        none_blood_disorders: [],
      },
    }),
  },

  // Q9: Serious Medical Conditions (exclusion)
  {
    id: "serious_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pheochromocytoma",
        label: "Known or suspected pheochromocytoma",
        disqualify: true,
      },
      {
        value: "eating_disorder",
        label: "Eating disorder as diagnosed by a doctor or psychologist",
        disqualify: true,
      },
      {
        value: "multiple_sclerosis",
        label: "Multiple sclerosis (MS)",
        disqualify: true,
      },
      {
        value: "liver_failure",
        label: "Liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "eskd",
        label: "End-stage kidney disease (ESKD) or currently receiving dialysis",
        disqualify: true,
      },
      {
        value: "none_medical",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        eating_disorder: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_EATING_DISORDER, booleanAttributeValue: true },
        ],
        multiple_sclerosis: [
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_MULTIPLE_SCLEROSIS, booleanAttributeValue: true },
        ],
        liver_failure: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
        eskd: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_medical",
      noneAttributeMap: {
        none_medical: [
          { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_EATING_DISORDER, booleanAttributeValue: false },
          { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_MULTIPLE_SCLEROSIS, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  // Q10: Cancer and Pregnancy (exclusion)
  {
    id: "cancer_pregnancy",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "current_cancer_treatment",
        label: "I am currently receiving treatment for cancer",
        disqualify: true,
      },
      {
        value: "pregnant_or_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      {
        value: "none_cancer_pregnancy",
        label: "None of the above",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include participants who are pregnant, breastfeeding, or currently receiving cancer treatment.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        current_cancer_treatment: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        pregnant_or_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_cancer_pregnancy",
      noneAttributeMap: {
        none_cancer_pregnancy: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
        ],
      },
    }),
  },

  // Q11: Clinical Trial Participation (exclusion)
  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question:
      "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not enrolling in or participating in any other clinical trials",
      },
      {
        value: "yes",
        label: "Yes, I am currently enrolling in or participating in another clinical trial",
        disqualify: true,
      },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
];
